import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';

const AreaManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const toast = useToast();
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    active: true,
  });

  useEffect(() => {
    fetchAreas();
  }, []);

  const fetchAreas = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/areas');
      setAreas(response.data);
    } catch (error) {
      console.error('Error fetching areas:', error);
      toast.error('Failed to load areas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (area = null) => {
    if (area) {
      setEditMode(true);
      setSelectedArea(area);
      setFormData({
        name: area.name || '',
        description: area.description || '',
        active: area.active !== undefined ? area.active : true,
      });
    } else {
      setEditMode(false);
      setSelectedArea(null);
      setFormData({
        name: '',
        description: '',
        active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedArea(null);
    setFormData({
      name: '',
      description: '',
      active: true,
    });
  };

  const handleSubmit = async () => {
    if (!formData.name || formData.name.trim() === '') {
      toast.error('Area name is required');
      return;
    }

    try {
      if (editMode) {
        await api.put(`/admin/areas/${selectedArea.id}`, formData);
        toast.success('Area updated successfully!');
      } else {
        await api.post('/admin/areas', formData);
        toast.success('Area created successfully!');
      }
      fetchAreas();
      setTimeout(() => {
        handleCloseDialog();
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save area');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this area? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/admin/areas/${id}`);
      toast.success('Area deleted successfully!');
      fetchAreas();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete area');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocationOnIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: '#0D47A1' }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#0D47A1',
              fontSize: { xs: '1.75rem', sm: '2.125rem' },
            }}
          >
            Area Management
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
            textTransform: 'none',
            borderRadius: 2,
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 1.5 },
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          Add Area
        </Button>
      </Box>


      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
          overflowX: 'auto',
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Area Name
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Description
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Status
              </TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {areas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No areas found. Click "Add Area" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              areas.map((area) => (
                <TableRow key={area.id} hover>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {area.name}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {area.description || '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={area.active ? 'Active' : 'Inactive'}
                      color={area.active ? 'success' : 'default'}
                      size="small"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(area)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(area.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
          },
        }}
      >
        <DialogTitle
          sx={{
            background: editMode
              ? 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)'
              : 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
          }}
        >
          {editMode ? 'Edit Area' : 'Add New Area'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Area Name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                helperText="Enter a unique area name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                helperText="Optional description for this area"
              />
            </Grid>
            <Grid item xs={12}>
              <Chip
                label={formData.active ? 'Active' : 'Inactive'}
                color={formData.active ? 'success' : 'default'}
                onClick={() => setFormData({ ...formData, active: !formData.active })}
                sx={{ cursor: 'pointer' }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Click to toggle status
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              background: editMode
                ? 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)'
                : 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
              textTransform: 'none',
              borderRadius: 2,
            }}
          >
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AreaManagement;

